/**
 * Utility functions for Homepage component
 * Handles app configuration and availability logic
 */

/**
 * Get department apps for a user
 * @param {Object} user - User object
 * @param {Array} hospitals - Array of hospitals
 * @param {Array} allApps - Array of all available apps
 * @returns {Array} Array of unique department apps
 */
const getDepartmentApps = (user, hospitals, allApps) => {
  if (!user || !hospitals || !allApps) return [];

  // Get all departments from all hospitals
  const allDepartments = hospitals.flatMap(hospital =>
    hospital.departments?.map(dept => ({
      ...dept,
      hospitalName: hospital.nameHebrew,
      hospitalId: hospital._id,
      hospitalEnglishName: hospital.nameEnglish
    })) || []
  );

  // Get apps from departments
  const deptApps = [];
  allDepartments.forEach(department => {
    if (!department.apps || department.apps.length === 0) return;

    const apps = allApps.filter((app) =>
      department.apps.some((deptApp) => {
        if (typeof deptApp === 'string') {
          return deptApp === app._id || deptApp === app.key;
        }
        return deptApp._id === app._id || deptApp.key === app.key;
      })
    );

    deptApps.push(...apps);
  });

  // Remove duplicates
  const uniqueApps = Array.from(new Map(deptApps.map(app => [app._id, app])).values());
  return uniqueApps;
};

/**
 * Get default tiles when user doesn't have a configuration
 * @param {Array} permittedApps - Apps the user is admin on
 * @param {Array} departmentApps - Apps from user's departments
 * @returns {Array} Array of default tiles (up to 6)
 */
const getDefaultTiles = (permittedApps, departmentApps) => {
  const tiles = [];
  let order = 0;

  // Add protocols and checklists first
  tiles.push({ type: 'protocols', order: order++ });
  tiles.push({ type: 'checklists', order: order++ });

  // Add permitted apps (apps the user is admin on)
  if (permittedApps && permittedApps.length > 0) {
    permittedApps.forEach(app => {
      if (tiles.length < 6) {
        tiles.push({ type: 'app', appId: app._id, order: order++ });
      }
    });
  }

  // Add department apps to complete up to 6
  if (departmentApps && departmentApps.length > 0) {
    departmentApps.forEach(app => {
      if (tiles.length < 6) {
        // Check if this app is not already in permitted apps
        const alreadyAdded = tiles.some(t => t.type === 'app' && (t.appId === app._id || t.appId?._id === app._id));
        if (!alreadyAdded) {
          tiles.push({ type: 'app', appId: app._id, order: order++ });
        }
      }
    });
  }

  return tiles;
};

/**
 * Get user's configured apps (or default apps if not configured)
 * @param {Object} userConfig - User configuration object
 * @param {Array} permittedApps - Apps the user is admin on
 * @param {Object} user - User object
 * @param {Array} hospitals - Array of hospitals
 * @param {Array} allApps - Array of all available apps
 * @returns {Array} Array of tiles to display
 */
export const getUserConfiguredApps = (userConfig, permittedApps, user, hospitals, allApps) => {
  // If user has configured tiles, use them
  if (userConfig?.homepage?.tiles?.length > 0) {
    return userConfig.homepage.tiles;
  }

  // Otherwise, get department apps and return default tiles
  const departmentApps = getDepartmentApps(user, hospitals, allApps);
  return getDefaultTiles(permittedApps, departmentApps);
};

/**
 * Get available apps for the edit homepage modal
 * Filters out apps in development and checks user permissions
 * @param {Array} allApps - Array of all available apps
 * @param {Object} user - User object
 * @returns {Array} Array of available tile options
 */
export const getAvailableApps = (allApps, user) => {
  // Filter out apps that are in development
  const availableApps = allApps ? allApps.filter(app => app.status !== 'inDevelopment') : [];

  // Check if user has permission to see users (superAdmin or admin on at least one hospital)
  const hasUsersPermission = user && (user.isSuperAdmin || user.roles?.some((role) => role.role === 'ADMIN'));

  // Build base options
  const baseOptions = [
    { type: 'protocols', title: 'פרוטוקולים', id: 'protocols' },
    { type: 'checklists', title: 'רשימות', id: 'checklists' },
    { type: 'departmentDocuments', title: 'מסמכי המחלקה', id: 'departmentDocuments' },
    { type: 'cases', title: 'מקרים רפואיים', id: 'cases' },
  ];

  // Add users option only if user has permission
  if (hasUsersPermission) {
    baseOptions.splice(1, 0, { type: 'users', title: 'משתמשים', id: 'users' });
  }

  // Add all available apps
  const appOptions = availableApps.map(app => ({
    type: 'app',
    appId: typeof app._id === 'string' ? app._id : app._id?.toString(),
    title: app.title,
    id: `app-${typeof app._id === 'string' ? app._id : app._id?.toString()}`,
    iconName: app.icon
  }));

  return [...baseOptions, ...appOptions];
};
